import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { environment } from '@environments/environment';
import {
  NgbCollapseModule,
  NgbDropdownModule,
} from '@ng-bootstrap/ng-bootstrap';
import { TieredMenu, TieredMenuModule } from 'primeng/tieredmenu';

import { User } from '../../models/Core/User';
import { Idea } from '../../models/Sessions/Idea/Idea';
import { Session } from '../../models/Sessions/Session';
import { AuthenticationService } from '../../services/auth/authentication.service';
import { BusService } from '../../services/bus.service';
import { ConfigService } from '../../services/config.service';
import { EventsService } from '../../services/events.service';
import { ProfilePictureComponent } from '../account/profile/profile-picture/profile-picture.component';
import { NotificationCenterComponent } from '../navbar/notification-center/notification-center.component';
import { SearchbarComponent } from '../navbar/searchbar/searchbar.component';

interface Project {
  topicId: string;
  idea: Idea;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    NgbCollapseModule,
    NgbDropdownModule,
    ProfilePictureComponent,
    NotificationCenterComponent,
    SearchbarComponent,
    TieredMenuModule,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  host: {
    class: 'border-end h-100 bg-light-subtle',
  },
})
export class SidebarComponent implements OnInit, OnDestroy {
  featureFlags = environment.featureFlags;
  createProjectMenuModel = [
    {
      label: 'Create Project',
      icon: 'pi pi-plus',
      items: [
        this.isPodcastCreationAllowed
          ? {
              label: 'Podcast',
              icon: 'pi pi-microphone',
              routerLink: '/project/new',
              queryParams: { type: 'podcast', topic: '' },
            }
          : null,
        {
          label: 'Article',
          icon: 'pi pi-file',
          routerLink: '/project/new',
          queryParams: { type: 'article', topic: '' },
        },
        this.isCustomStyleCreationAllowed
          ? {
              label: 'Provide Style Example',
              icon: 'pi pi-bolt',
              routerLink: '/project/new',
              queryParams: { type: 'custom-style', topic: '' },
            }
          : null,
      ].filter(Boolean),
    },
  ] satisfies TieredMenu['model'];

  isTopicsCollapsed = true;
  topics: (Session & { isCollapsed: boolean })[] = [];
  defaultTopic: Session | null = null;

  isRecentCollapsed = true;
  publishedProjects: Project[] = [];
  unpublishedProjects: Project[] = [];

  isFollowedCollapsed = true;
  followedProjects: Project[] = [];

  currentUser: User | null = null;

  get isPodcastCreationAllowed() {
    return (
      this.featureFlags.projectCreation.topicInput ||
      this.featureFlags.projectCreation.textInput
    );
  }

  get isCustomStyleCreationAllowed() {
    return this.featureFlags.projectCreation.customStyleInput;
  }

  constructor(
    private bus: BusService,
    private events: EventsService,
    private config: ConfigService,
    public auth: AuthenticationService,
  ) {}

  ngOnInit(): void {
    this.bus.subscribe(
      this.events.received.data.session.all.success,
      this.updateSessions,
      this,
    );
    this.bus.subscribe(
      this.events.received.data.user.me.success,
      this.setCurrentUser,
      this,
    );

    this.bus.subscribe(
      this.events.ui.context.topic,
      this.updateCreateProjectMenu,
      this,
    );

    this.bus.publish(this.events.requested.data.user.me);
  }

  setCurrentUser(user: User) {
    if (!this.currentUser) {
      this.bus.publish(this.events.requested.data.session.all);
    }
    this.currentUser = user;
  }

  toggleTopicsCollapse() {
    if (!this.isTopicsCollapsed) {
      this.topics.forEach(t => (t.isCollapsed = true));
    }

    this.isTopicsCollapsed = !this.isTopicsCollapsed;
  }

  updateSessions(sessions) {
    this.topics = sessions
      .filter(session => !session.config.deactivated)
      .filter(
        session =>
          !session.config.restricted ||
          session.me.role <= this.config.roles.SESSION_MODERATOR,
      )
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      )
      .map(session => ({
        ...session,
        data: {
          ...session.data,
          ideas: session.data.ideas
            .filter(idea => !idea.deleted_at)
            .sort(
              (a, b) =>
                new Date(b.created_at).getTime() -
                new Date(a.created_at).getTime(),
            ),
        },
        me: session.config.participants.find(
          participant => participant.user_id === this.currentUser._id,
        ),
        isCollapsed: true,
      }));

    this.updateIdeas();
    this.updateDefaultTopic();
  }

  private updateDefaultTopic() {
    if (!this.currentUser) return;
    const { default_session_id } = this.currentUser;

    if (!default_session_id) {
      this.defaultTopic = null;
      return;
    }

    this.defaultTopic = this.topics.find(
      topic => topic._id === default_session_id,
    );
  }

  requestLogout() {
    this.bus.publish(this.events.notified.user.logout);
  }

  updateIdeas() {
    const personal = (projects: Project[]): Project[] => {
      return projects.filter(
        ({ idea }) =>
          idea.created_by === this.currentUser._id ||
          idea.collaborators?.some(
            collaborator =>
              collaborator.participant_id === this.currentUser._id,
          ),
      );
    };

    const recent = (projects: Project[]): Project[] => {
      return projects
        .slice(0, 10)
        .sort(
          (a, b) =>
            (this.ideaLID(b.idea) as any) - (this.ideaLID(a.idea) as any),
        );
    };

    const followed = (ideas: Project[]): Project[] => {
      return ideas.filter(({ idea }) =>
        idea.followers?.some(
          follower => follower.participant_id === this.currentUser._id,
        ),
      );
    };

    const projects = this.topics
      .map(session =>
        session.data.ideas.reduce((acc: Project[], idea) => {
          acc.push({ topicId: session._id, idea });
          return acc;
        }, []),
      )
      .flat();

    const personalProjects = personal(projects);

    this.publishedProjects = recent(personalProjects).filter(
      ({ idea }) => idea.published,
    );
    this.unpublishedProjects = recent(personalProjects).filter(
      ({ idea }) => !idea.published,
    );
    this.followedProjects = followed(projects);
  }

  updateCreateProjectMenu(topicId: string) {
    this.createProjectMenuModel = [
      {
        label: 'Create Project',
        icon: 'pi pi-plus',
        items: this.createProjectMenuModel[0].items.map(item => ({
          ...item,
          queryParams: {
            ...item.queryParams,
            topic: topicId,
          },
        })),
      },
    ];
  }

  //
  // this function will calculate the last interaction date (by anyone)
  // with an idea.
  //
  ideaLID(idea: Idea): Date {
    let lid = new Date(idea.last_changed);
    idea.comments.forEach(comment => {
      const date = new Date(comment.created_at);
      if (date > lid) {
        lid = date;
      }
    });

    return lid;
  }

  ngOnDestroy(): void {
    this.bus.unsubscribe(
      this.events.received.data.session.all.success,
      this.updateSessions,
    );
    this.bus.unsubscribe(
      this.events.received.data.user.me.success,
      this.setCurrentUser,
    );

    this.bus.unsubscribe(
      this.events.ui.context.topic,
      this.updateCreateProjectMenu,
    );
  }
}
